import { motion } from "framer-motion";
import { FileCheck, Search, Network, Shield, CheckCircle, Globe, AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

// 流程模块组件 - 固定高度
const ProcessModule = ({ 
  icon: Icon, 
  title, 
  active, 
  complete, 
  children 
}: { 
  icon: React.ElementType; 
  title: string; 
  active: boolean; 
  complete: boolean; 
  children: React.ReactNode;
}) => (
  <motion.div
    className={`relative rounded-lg p-3 border transition-all duration-300 h-[140px] flex flex-col ${
      active 
        ? "border-blue-400/60 bg-blue-500/10" 
        : complete 
          ? "border-emerald-400/40 bg-emerald-500/5" 
          : "border-slate-600/30 bg-slate-800/30"
    }`}
  >
    <div className="flex items-center gap-2 mb-2">
      <div className={`p-1.5 rounded-md ${
        active ? "bg-blue-500/20" : complete ? "bg-emerald-500/20" : "bg-slate-700/50"
      }`}>
        <Icon className={`w-3.5 h-3.5 ${
          active ? "text-blue-400" : complete ? "text-emerald-400" : "text-slate-400"
        }`} />
      </div>
      <span className={`text-xs font-medium ${
        active ? "text-blue-300" : complete ? "text-emerald-300" : "text-slate-400"
      }`}>
        {title}
      </span>
      {complete && (
        <CheckCircle className="w-3 h-3 text-emerald-400 ml-auto" />
      )}
    </div>
    <div className="flex-1 overflow-hidden">
      {children}
    </div>
  </motion.div>
);

// HS Code建议内容
const HSCodeContent = ({ active, complete }: { active: boolean; complete: boolean }) => {
  const { t } = useLanguage();
  const [dailyCount, setDailyCount] = useState(0);
  
  useEffect(() => {
    if (complete) {
      const interval = setInterval(() => {
        setDailyCount(prev => Math.min(prev + 47, 1247));
      }, 50);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [complete]);

  return (
    <div className="h-full flex flex-col justify-center">
      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-1.5"
        >
          <div className="flex items-center gap-2">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full"
            />
            <span className="text-[10px] text-blue-300">{t("customsAnim.analyzing")}</span>
          </div>
          <div className="bg-slate-800/50 rounded p-1.5">
            <div className="text-[9px] text-slate-400 mb-1">{t("customsAnim.product")}</div>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.5 }}
              className="h-1 bg-gradient-to-r from-blue-500 to-cyan-400 rounded"
            />
          </div>
        </motion.div>
      )}
      {complete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="space-y-1.5"
        >
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-emerald-300 font-medium">8542.31</span>
              <span className="text-[9px] text-emerald-400">98.5% {t("customsAnim.match")}</span>
            </div>
            <div className="text-[8px] text-slate-400 mt-0.5">{t("customsAnim.processorController")}</div>
          </div>
          <div className="text-center">
            <span className="text-lg font-bold text-emerald-400">{dailyCount.toLocaleString()}</span>
            <span className="text-[9px] text-slate-400 ml-1">{t("customsAnim.todaySuggestions")}</span>
          </div>
        </motion.div>
      )}
      {!active && !complete && (
        <div className="flex items-center justify-center h-full">
          <span className="text-[10px] text-slate-500">{t("customsAnim.waitingProcess")}</span>
        </div>
      )}
    </div>
  );
};

// 制裁名单筛查内容
const SanctionListContent = ({ active, complete }: { active: boolean; complete: boolean }) => {
  const { t } = useLanguage();
  const [passCount, setPassCount] = useState(0);
  const entities = ["ACME Corp", "Global Trade Ltd", "TechFlow Inc", "Marine Shipping"];
  
  useEffect(() => {
    if (complete) {
      const interval = setInterval(() => {
        setPassCount(prev => Math.min(prev + 23, 892));
      }, 50);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [complete]);

  return (
    <div className="h-full flex flex-col justify-center">
      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-1"
        >
          <div className="text-[9px] text-blue-300 mb-1">{t("customsAnim.screeningEntities")}</div>
          <div className="space-y-0.5 max-h-16 overflow-hidden">
            {entities.map((entity, i) => (
              <motion.div
                key={entity}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: i * 0.2 }}
                className="flex items-center gap-1.5 text-[9px]"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, delay: i * 0.2 + 0.3 }}
                  className="w-3 h-3 rounded-full bg-emerald-500/20 flex items-center justify-center"
                >
                  <CheckCircle className="w-2 h-2 text-emerald-400" />
                </motion.div>
                <span className="text-slate-300">{entity}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
      {complete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="space-y-1.5"
        >
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-1.5 flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-400" />
            <div>
              <div className="text-[10px] text-emerald-300 font-medium">{t("customsAnim.noSanctionRisk")}</div>
              <div className="text-[8px] text-slate-400">{t("customsAnim.crossVerified")}</div>
            </div>
          </div>
          <div className="text-center">
            <span className="text-lg font-bold text-emerald-400">{passCount}</span>
            <span className="text-[9px] text-slate-400 ml-1">{t("customsAnim.todayPassed")}</span>
          </div>
        </motion.div>
      )}
      {!active && !complete && (
        <div className="flex items-center justify-center h-full">
          <span className="text-[10px] text-slate-500">{t("customsAnim.waitingProcess")}</span>
        </div>
      )}
    </div>
  );
};

// 树状图内容 - 海关法规知识图谱
const KnowledgeGraphContent = ({ active, complete }: { active: boolean; complete: boolean }) => {
  const { t } = useLanguage();
  const treeData = {
    root: { label: t("customsAnim.customsRegulations"), color: "cyan" },
    branches: [
      { label: "CN", children: [t("customsAnim.import"), t("customsAnim.export")] },
      { label: "US", children: ["FDA", "FCC"] },
      { label: "EU", children: ["CE", "REACH"] }
    ]
  };

  return (
    <div className="h-full flex flex-col justify-center">
      {(active || complete) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative h-full"
        >
          {/* 树状图 */}
          <svg className="w-full h-full" viewBox="0 0 160 80">
            {/* 根节点 */}
            <motion.g
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <rect x="60" y="2" width="40" height="14" rx="3" fill="rgba(34, 211, 238, 0.2)" stroke="rgba(34, 211, 238, 0.6)" strokeWidth="1" />
              <text x="80" y="12" textAnchor="middle" className="fill-cyan-300 text-[7px] font-medium">{treeData.root.label}</text>
            </motion.g>

            {/* 连接线到第二层 */}
            {[0, 1, 2].map((i) => (
              <motion.line
                key={`line1-${i}`}
                x1="80"
                y1="16"
                x2={25 + i * 55}
                y2="30"
                stroke="rgba(34, 211, 238, 0.4)"
                strokeWidth="1"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
              />
            ))}

            {/* 第二层节点 */}
            {treeData.branches.map((branch, i) => (
              <motion.g
                key={branch.label}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.5 + i * 0.15 }}
              >
                <rect 
                  x={10 + i * 55} 
                  y="30" 
                  width="30" 
                  height="12" 
                  rx="2" 
                  fill={complete ? "rgba(16, 185, 129, 0.2)" : "rgba(59, 130, 246, 0.2)"} 
                  stroke={complete ? "rgba(16, 185, 129, 0.6)" : "rgba(59, 130, 246, 0.6)"} 
                  strokeWidth="1" 
                />
                <text 
                  x={25 + i * 55} 
                  y="39" 
                  textAnchor="middle" 
                  className={`text-[6px] font-medium ${complete ? "fill-emerald-300" : "fill-blue-300"}`}
                >
                  {branch.label}
                </text>

                {/* 连接线到第三层 */}
                {branch.children.map((_, j) => (
                  <motion.line
                    key={`line2-${i}-${j}`}
                    x1={25 + i * 55}
                    y1="42"
                    x2={12 + i * 55 + j * 26}
                    y2="55"
                    stroke={complete ? "rgba(16, 185, 129, 0.3)" : "rgba(59, 130, 246, 0.3)"}
                    strokeWidth="1"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.8 + i * 0.1 + j * 0.05 }}
                  />
                ))}

                {/* 第三层节点 */}
                {branch.children.map((child, j) => (
                  <motion.g
                    key={child}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: 1 + i * 0.15 + j * 0.1 }}
                  >
                    <rect 
                      x={2 + i * 55 + j * 26} 
                      y="55" 
                      width="22" 
                      height="10" 
                      rx="2" 
                      fill="rgba(148, 163, 184, 0.15)" 
                      stroke="rgba(148, 163, 184, 0.4)" 
                      strokeWidth="0.5" 
                    />
                    <text 
                      x={13 + i * 55 + j * 26} 
                      y="62" 
                      textAnchor="middle" 
                      className="fill-slate-400 text-[5px]"
                    >
                      {child}
                    </text>
                  </motion.g>
                ))}
              </motion.g>
            ))}

            {/* 高亮动画 */}
            {active && (
              <motion.circle
                r="3"
                fill="rgba(34, 211, 238, 0.8)"
                initial={{ cx: 80, cy: 9 }}
                animate={{
                  cx: [80, 25, 80, 135, 80],
                  cy: [9, 36, 9, 36, 9]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                style={{
                  filter: "drop-shadow(0 0 4px rgba(34, 211, 238, 0.8))"
                }}
              />
            )}
          </svg>
        </motion.div>
      )}
      {!active && !complete && (
        <div className="flex items-center justify-center h-full">
          <span className="text-[10px] text-slate-500">{t("customsAnim.waitingProcess")}</span>
        </div>
      )}
    </div>
  );
};

// 合规检查内容
const ComplianceCheckContent = ({ active, complete }: { active: boolean; complete: boolean }) => {
  const { t } = useLanguage();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (active) {
      const interval = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 100));
      }, 100);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [active]);

  const checkItems = [t("customsAnim.hsVerify"), t("customsAnim.sanctionScreen"), t("customsAnim.regulationMatch"), t("customsAnim.riskAssess")];

  return (
    <div className="h-full flex flex-col justify-center">
      {active && !complete && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-2"
        >
          <div className="flex items-center justify-between text-[9px]">
            <span className="text-blue-300">{t("customsAnim.comprehensiveAnalysis")}</span>
            <span className="text-blue-400">{progress}%</span>
          </div>
          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-400"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {checkItems.map((item, i) => (
              <motion.span
                key={item}
                initial={{ opacity: 0 }}
                animate={{ opacity: progress > i * 25 ? 1 : 0.3 }}
                className="text-[8px] px-1.5 py-0.5 bg-slate-700/50 rounded text-slate-300"
              >
                {item}
              </motion.span>
            ))}
          </div>
        </motion.div>
      )}
      {complete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/10 border border-emerald-500/30 rounded-lg p-2"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <div className="text-[11px] text-emerald-300 font-medium">{t("customsAnim.reportGenerated")}</div>
              <div className="text-[8px] text-slate-400">{t("customsAnim.autoCheckComplete")}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1 text-center">
            {[
              { label: t("customsAnim.riskLevel"), value: t("customsAnim.low") },
              { label: t("customsAnim.confidence"), value: "98%" },
              { label: t("customsAnim.timeTaken"), value: "1.2s" }
            ].map(item => (
              <div key={item.label} className="bg-slate-800/50 rounded p-1">
                <div className="text-[10px] text-emerald-400 font-medium">{item.value}</div>
                <div className="text-[7px] text-slate-500">{item.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
      {!active && !complete && (
        <div className="flex items-center justify-center h-full">
          <span className="text-[10px] text-slate-500">{t("customsAnim.waitingProcess")}</span>
        </div>
      )}
    </div>
  );
};

export const CustomsEmployeeCard = () => {
  const { t } = useLanguage();
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const sequence = [
      { delay: 500, phase: 1 },   // HS Code 激活
      { delay: 2000, phase: 2 },  // HS Code 完成, 制裁筛查激活
      { delay: 2000, phase: 3 },  // 制裁筛查完成, 知识图谱激活
      { delay: 2000, phase: 4 },  // 知识图谱完成, 合规检查激活
      { delay: 2500, phase: 5 },  // 全部完成
      { delay: 3000, phase: 0 },  // 重置
    ];

    let timeouts: ReturnType<typeof setTimeout>[] = [];
    let accumulated = 0;

    sequence.forEach(({ delay, phase: p }) => {
      accumulated += delay;
      timeouts.push(setTimeout(() => setPhase(p), accumulated));
    });

    const totalDuration = accumulated;
    const loopInterval = setInterval(() => {
      accumulated = 0;
      sequence.forEach(({ delay, phase: p }) => {
        accumulated += delay;
        timeouts.push(setTimeout(() => setPhase(p), accumulated));
      });
    }, totalDuration);

    return () => {
      timeouts.forEach(clearTimeout);
      clearInterval(loopInterval);
    };
  }, []);

  return (
    <div className="relative w-full h-[420px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl overflow-hidden border border-slate-700/50">
      {/* 背景装饰 */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      {/* 标题 */}
      <div className="relative p-4 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30">
            <Globe className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">{t("customsWorker.title")}</h3>
            <p className="text-[10px] text-slate-400">{t("customsAnim.subtitle")}</p>
          </div>
          {phase === 5 && (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              className="ml-auto flex items-center gap-1.5 px-2 py-1 bg-emerald-500/20 rounded-full border border-emerald-500/30"
            >
              <CheckCircle className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] text-emerald-300 font-medium">{t("customsAnim.processComplete")}</span>
            </motion.div>
          )}
        </div>
      </div>

      {/* 流程模块网格 - 无连线 */}
      <div className="relative p-4 grid grid-cols-2 gap-3">
        {/* HS Code 建议 */}
        <ProcessModule
          icon={FileCheck}
          title={t("customsWorker.h1")}
          active={phase === 1}
          complete={phase >= 2}
        >
          <HSCodeContent active={phase === 1} complete={phase >= 2} />
        </ProcessModule>

        {/* 制裁名单筛查 */}
        <ProcessModule
          icon={Search}
          title={t("customsWorker.h2")}
          active={phase === 2}
          complete={phase >= 3}
        >
          <SanctionListContent active={phase === 2} complete={phase >= 3} />
        </ProcessModule>

        {/* 海关法规知识图谱 - 树状图 */}
        <ProcessModule
          icon={Network}
          title={t("customsWorker.h3")}
          active={phase === 3}
          complete={phase >= 4}
        >
          <KnowledgeGraphContent active={phase === 3} complete={phase >= 4} />
        </ProcessModule>

        {/* 合规检查 */}
        <ProcessModule
          icon={Shield}
          title={t("customsWorker.h4")}
          active={phase === 4}
          complete={phase === 5}
        >
          <ComplianceCheckContent active={phase === 4} complete={phase === 5} />
        </ProcessModule>
      </div>

      {/* 底部状态栏 */}
      <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-700/50 bg-slate-900/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${phase > 0 && phase < 5 ? "bg-blue-400 animate-pulse" : phase === 5 ? "bg-emerald-400" : "bg-slate-600"}`} />
              <span className="text-[10px] text-slate-400">
                {phase === 0 ? t("customsAnim.standby") : phase === 5 ? t("customsAnim.completed") : t("customsAnim.processing")}
              </span>
            </div>
            {phase > 0 && phase < 5 && (
              <div className="text-[9px] text-slate-500">
                {t("customsAnim.step")} {Math.min(phase, 4)}/4
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3 h-3 text-amber-400/50" />
            <span className="text-[9px] text-slate-500">{t("customsAnim.monitoring")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomsEmployeeCard;
